import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { getLLMConfiguration } from '@/api/llm.api';
import {
  applySubsystemDefaults,
  buildDefaultLLMConfig,
  isLLMConfigurationEmpty,
  resolveLLMConfiguration,
  type ResolvedLLMConfig,
} from '@/helpers/llmConfiguration.helpers';
import { logger } from '@/utils/logger';

interface LLMConfigContextType {
  config: ResolvedLLMConfig;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const LLMConfigContext = createContext<LLMConfigContextType | undefined>(
  undefined,
);

export const LLMConfigProvider = ({ children }: { children: ReactNode }) => {
  const [config, setConfig] = useState<ResolvedLLMConfig>(() =>
    buildDefaultLLMConfig(),
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  // Several consumer pages call refetch() on mount. When two of them mount
  // in the same tick (e.g. ModelDesigner + a child component) we want a
  // single network call, not N — hand back the in-flight promise instead
  // of starting a parallel request.
  const inFlightRef = useRef<Promise<void> | null>(null);

  const refetch = useCallback(async () => {
    if (inFlightRef.current) {
      return inFlightRef.current;
    }
    setLoading(true);
    setError(null);
    const request = (async () => {
      try {
        const raw = await getLLMConfiguration();
        if (isLLMConfigurationEmpty(raw)) {
          setConfig(buildDefaultLLMConfig());
        } else {
          setConfig(applySubsystemDefaults(resolveLLMConfiguration(raw)));
        }
      } catch (err) {
        logger.error(
          '[LLMConfigContext] Failed to load LLM configuration',
          err,
        );
        setError(
          err instanceof Error
            ? err
            : new Error('Failed to fetch LLM configuration'),
        );
        setConfig(buildDefaultLLMConfig());
      } finally {
        setLoading(false);
        inFlightRef.current = null;
      }
    })();
    inFlightRef.current = request;
    return request;
  }, []);

  // Memoized so non-config provider re-renders don't cascade re-renders
  // through every consumer.
  const value = useMemo<LLMConfigContextType>(
    () => ({ config, loading, error, refetch }),
    [config, loading, error, refetch],
  );

  return (
    <LLMConfigContext.Provider value={value}>
      {children}
    </LLMConfigContext.Provider>
  );
};

export const useLLMConfig = () => {
  const ctx = useContext(LLMConfigContext);
  if (!ctx) {
    throw new Error('useLLMConfig must be used within an LLMConfigProvider');
  }
  return ctx;
};
